/**
 * Rule-based answer engine for the portfolio assistant. No LLM: intents are
 * scored by fuzzy keyword/phrase matching, projects and technologies are looked
 * up from the real site data, and a small conversation context handles
 * follow-ups like "tell me more".
 *
 * createAssistant({ projects }) → { ask(message, context) → { text, links, followUps, intent } }
 */
import * as K from './knowledge.js';
import { buildIndex } from './retrieval.js';

// ---------- text utilities ----------
const STOP = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'do', 'does', 'did', 'can', 'could', 'you', 'your', 'he', 'his', 'him', 'me', 'i', 'to', 'of', 'in', 'on', 'for', 'and', 'or', 'about', 'tell', 'what', 'which', 'who', 'how', 'please', 'know', 'with', 'at', 'it', 'this', 'that', 'any', 'some', 'have', 'has', 'be', 'rakshan', 'rakshans', 'shetty', 'u', 'ur']);

export const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9+#.\s/-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokens = (text) => normalize(text).split(' ').filter(Boolean);

const editDistance = (a, b) => {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
};

/** True when `token` is the keyword, a plural/stem of it, or a near typo. */
const fuzzyEq = (token, keyword) => {
  if (token === keyword) return true;
  if (keyword.length >= 4 && (token.startsWith(keyword) || keyword.startsWith(token)) && Math.abs(token.length - keyword.length) <= 3 && token.length >= 4) return true;
  if (keyword.length >= 5 && editDistance(token, keyword) <= (keyword.length >= 8 ? 2 : 1)) return true;
  return false;
};

const hasPhrase = (norm, phrase) => norm.includes(phrase);

/**
 * Intent scoring. Keywords are compiled once into a hash map (keyword → intents).
 * A word that is a keyword is an O(1) hit; other words are fuzzy-matched once and
 * the result is cached per word, so repeated words cost nothing on later questions.
 */
const compileIntents = (intents) => {
  const kwToIntents = new Map();
  const multi = intents.map(() => []);
  intents.forEach((it, idx) => {
    for (const kw of it.keywords || []) {
      if (kw.includes(' ')) { multi[idx].push(kw); continue; }
      if (!kwToIntents.has(kw)) kwToIntents.set(kw, new Set());
      kwToIntents.get(kw).add(idx);
    }
  });
  const allKeywords = [...kwToIntents.keys()];
  const wordCache = new Map();
  const intentsForWord = (t) => {
    if (wordCache.has(t)) return wordCache.get(t);
    const hits = new Set(kwToIntents.get(t) || []);
    if (t.length >= 4) {
      for (const kw of allKeywords) {
        if (kw === t || Math.abs(kw.length - t.length) > 3) continue;
        if (fuzzyEq(t, kw)) for (const idx of kwToIntents.get(kw)) hits.add(idx);
      }
    }
    if (wordCache.size > 2000) wordCache.clear();
    wordCache.set(t, hits);
    return hits;
  };
  const scoreAll = (norm, toks) => {
    const counts = new Array(intents.length).fill(0);
    for (const t of new Set(toks)) for (const idx of intentsForWord(t)) counts[idx] += 1;
    return intents.map((it, idx) => {
      let score = counts[idx] * 2;
      for (const phrase of it.phrases || []) if (hasPhrase(norm, phrase)) score += 3;
      for (const kw of multi[idx]) if (hasPhrase(norm, kw)) score += 2.5;
      return { i: it, s: score * (it.weight || 1) };
    });
  };
  const isKeyword = (w) => kwToIntents.has(w);
  return { scoreAll, isKeyword };
};

// ---------- formatting helpers ----------
const list = (items) => items.map((i) => `• ${i}`).join('\n');
const contactLinks = () => [
  { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` },
  { label: 'GitHub', url: `https://${K.contactInfo.github}` },
  { label: 'Download resume', url: K.contactInfo.resume },
];


// ---------- role fit (hiring-manager questions like "is he a good fit for a backend role?") ----------
const ROLE_FITS = [
  { test: /full ?stack|end to end/, title: 'full stack', points: [
    'He builds both sides: React and Next.js front ends with server-side rendering, and Node.js and Python back ends with REST APIs.',
    'He handles the data layer (PostgreSQL, MongoDB, Redis) and the path to production (Docker, AWS, GitHub Actions).',
    'He shipped 3 React and Next.js apps used by 500+ active users, and cut page load from 5s to 0.5s.',
  ] },
  { test: /back ?end|api|server|database/, title: 'backend', points: [
    'He designs REST APIs and data models. At Anave he wrote the APIs for order tracking, split payments, affiliate marketing and loyalty points, and integrated the payment gateway and Shiprocket.',
    'At Foxwel.ai he defines the data model and API contracts for a multi-module business platform.',
    "He works with PostgreSQL, MongoDB and Redis, JWT auth and role-based access control, and built the Node.js and TypeScript backend of Baton, his open-source agent orchestration tool.",
  ] },
  { test: /front ?end|\bui\b|react|web developer/, title: 'frontend', points: [
    'React and Next.js are his daily tools, with TypeScript, Tailwind CSS and server-side rendering.',
    'He shipped 3 production web apps used by 500+ active users and brought page load down from 5s to 0.5s.',
    'He cares about the details: this portfolio, its animations and this chatbot are his own work.',
  ] },
  { test: /gen ?ai|\bai\b|llm|machine learning|\bml\b|agent|rag|data scien/, title: 'GenAI or AI engineering', points: [
    'He has GenAI in production: a RAG chatbot over a live catalog and order history with LangChain and LangSmith, and the Gemini API built into a business platform.',
    'He builds tools for AI coding agents: Baton coordinates several agents on one repo, and Elmory gives them memory that checks itself against the code.',
    'His degree is in AI and ML, with models for CT-scan stroke prediction (96% accuracy) and music genre classification, plus two AI/ML internships.',
  ] },
  { test: /mobile|react native|android|ios|app developer/, title: 'mobile', points: [
    'He has shipped React Native apps, including one with real-time chat and video calling.',
    'He pairs that with the backend the app needs, so a mobile feature does not wait on another team.',
  ] },
  { test: /lead|architect|senior|principal/, title: 'lead or architect', points: [
    'He leads system architecture and design at Foxwel.ai and guides the team through delivery.',
    'He led architecture and design for HobbyistDecals.com at Anave, and led the team that built his college alumni portal.',
    'He has about 2 years of production experience, so he fits a senior or lead role on a product team better than a staff role at a large company.',
  ] },
  { test: /devops|infra|cloud|sre|platform/, title: 'DevOps or platform', points: [
    'He sets up CI/CD with GitHub Actions (60% faster deploys), containerises with Docker and deploys on AWS (EC2, S3, SES), Vercel and Netlify.',
    "Kubernetes and Terraform aren't in his stack, so he suits product teams where engineers own their deploys better than a dedicated SRE role.",
  ] },
];

const roleFit = (norm) => {
  const role = ROLE_FITS.find((r) => r.test.test(norm));
  if (!role) {
    return {
      text: `He's strongest for **full stack**, **backend** and **GenAI** engineering roles, and a good fit for teams that are adopting AI coding agents.\n\nTell me the role, for example "is he a fit for a backend role?", and I'll give you the specifics. Or reach him directly at **${K.contactInfo.email}**.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
      followUps: ['Is he a good fit for a backend role?', 'Is he a good fit for a GenAI role?', 'Is he a good fit for a full stack role?'],
    };
  }
  return {
    text: `**Yes, he's a strong fit for a ${role.title} role.** Here's why:\n${list(role.points)}\n\nWant to talk it through with him? **${K.contactInfo.email}**`,
    links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }, { label: 'Download resume', url: K.contactInfo.resume }],
    followUps: ['Why should we hire him?', 'How experienced is he?', 'Can I set up a call?'],
  };
};

// Questions about how Baton is built get the product story, never the internals.
const BATON_INTERNALS = /\b(implement\w*|internals?|under the hood|algorithms?|source code|codebase of|architecture|daemon|worktrees?|sse|hash\w*|data structures?|database schema|reverse engineer\w*|how (is|was) (it|baton) (built|coded|made|written)|how does (it|baton) (detect|know|track|lock|store|sync)\w*)\b/;

const batonInternals = () => ({
  text: `I keep Baton's implementation details out of this chat. Here's what it does for a team:\n${list([
    '**Claude Code plans and reviews**, while Cursor, Codex, Gemini and free models build in parallel.',
    '**10 to 15 agents on one codebase** with no overwrites: an agent waits when another is editing the same file.',
    '**One shared knowledge base**, so every agent starts already knowing the project, in about 1,000 tokens.',
  ])}\n\nFor a technical deep dive, ${K.profile.firstName} is happy to walk through the design on a call: **${K.contactInfo.email}**`,
  links: [K.baton.links[2], { label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
  followUps: ['Why did he build Baton?', 'Can I set up a call?', 'What is Elmory?'],
  noOpener: true,
});

// ---------- intents ----------
const INTENTS = [
  {
    id: 'greeting',
    keywords: ['hello', 'hi', 'hey', 'hola', 'namaste', 'yo', 'morning', 'evening', 'afternoon', 'sup'],
    phrases: ['good morning', 'good evening', 'whats up'],
    weight: 0.9,
    answer: () => ({
      text: `Hi! I'm ${K.profile.firstName}'s portfolio assistant. He's a **${K.profile.title}** at **${K.profile.company}** with ${K.profile.yearsExperience} years of experience building full-stack and AI products.\n\nAsk me about his experience, projects, skills, education, or how to reach him.`,
      followUps: ['What does he do at Foxwel.ai?', 'What projects has he built?', 'How can I contact him?'],
    }),
  },
  {
    id: 'about',
    keywords: ['who', 'about', 'introduce', 'introduction', 'yourself', 'himself', 'summary', 'overview', 'bio', 'profile'],
    phrases: ['who is rakshan', 'who are you', 'about rakshan', 'about him', 'tell me about yourself', 'what do you do', 'what does he do'],
    answer: () => ({
      text: `**${K.profile.name}** — ${K.profile.title} at ${K.profile.company}, based in ${K.profile.location}.\n\n${K.profile.summary}\n\n**Quick facts**\n${list([
        `${K.profile.yearsExperience} years of professional experience`,
        'B.E. in Computer Science (AI & ML), CGPA 8.5',
        'Led system architecture at Foxwel.ai and for HobbyistDecals.com at Anave; maintainer of Baton (open source)',
        K.profile.availability,
      ])}`,
      links: contactLinks(),
      followUps: ['Tell me about his experience', 'What is Baton?', 'Is he available for hire?'],
    }),
  },
  {
    id: 'bot-identity',
    keywords: ['bot', 'robot', 'human', 'ai', 'chatgpt', 'gpt', 'llm', 'model', 'real'],
    phrases: ['are you a bot', 'are you human', 'are you real', 'are you an ai', 'which model', 'are you chatgpt', 'is this a bot'],
    weight: 0.8,
    answer: () => ({
      text: `I'm a small rule-based assistant built into this portfolio — no external AI model, just curated facts about ${K.profile.firstName}. I answer from his real experience, projects and contact details, so you get accurate information while he's away.\n\nFor anything I can't answer, email him at **${K.contactInfo.email}**.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
      followUps: ['What can you tell me?', 'Tell me about his experience'],
    }),
  },
  {
    id: 'help',
    keywords: ['help', 'options', 'questions', 'topics', 'ask'],
    phrases: ['what can you', 'what can i ask', 'what do you know', 'how does this work'],
    answer: () => ({
      text: `I can tell you about:\n${list([
        '**Experience** — Foxwel.ai, Anave Enterprises, internships',
        '**Projects** — Baton, HobbyistDecals, Alumni Portal, ML systems and more',
        '**Skills** — any technology, e.g. "does he know Docker?"',
        '**Education & certifications**',
        '**Availability, work mode and location**',
        '**Contact** — email, phone, LinkedIn, GitHub, resume',
      ])}`,
      followUps: ['Does he know Next.js?', 'Tell me about Baton', 'How can I contact him?'],
    }),
  },
  {
    id: 'thanks',
    keywords: ['thanks', 'thank', 'thx', 'ty', 'appreciate', 'helpful', 'great', 'awesome', 'cool', 'nice'],
    weight: 0.8,
    answer: () => ({
      text: `You're welcome! If you'd like to take it further, ${K.profile.firstName} is quickest to reach by email at **${K.contactInfo.email}**.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }, { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` }],
    }),
  },
  {
    id: 'bye',
    keywords: ['bye', 'goodbye', 'later', 'cya', 'ciao'],
    phrases: ['see you', 'talk later', 'good night'],
    weight: 0.8,
    answer: () => ({ text: `Goodbye, and thanks for stopping by! ${K.profile.firstName}'s resume and links are one click away whenever you need them.`, links: contactLinks() }),
  },
  {
    id: 'contact',
    keywords: ['contact', 'reach', 'email', 'mail', 'gmail', 'phone', 'number', 'call', 'whatsapp', 'connect', 'touch', 'message', 'dm'],
    phrases: ['get in touch', 'how can i contact', 'reach out', 'reach him', 'contact details'],
    answer: () => ({
      text: `Here's how to reach ${K.profile.firstName}:\n${list([
        `**Email:** ${K.contactInfo.email} (fastest)`,
        `**Phone / WhatsApp:** ${K.contactInfo.phone}`,
        `**LinkedIn:** ${K.contactInfo.linkedin}`,
        `**GitHub:** ${K.contactInfo.github}`,
      ])}\n\nHe replies promptly to opportunities, collaborations and technical questions.`,
      links: [
        { label: 'Email', url: `mailto:${K.contactInfo.email}` },
        { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` },
        { label: 'GitHub', url: `https://${K.contactInfo.github}` },
        { label: 'Download resume', url: K.contactInfo.resume },
      ],
      followUps: ['Is he available for hire?', 'Where is he located?'],
    }),
  },
  {
    id: 'linkedin',
    keywords: ['linkedin'],
    weight: 1.3,
    answer: () => ({
      text: `${K.profile.firstName}'s LinkedIn: **${K.contactInfo.linkedin}**. It covers his roles, endorsements and network — feel free to send a connection request.`,
      links: [{ label: 'Open LinkedIn', url: `https://${K.contactInfo.linkedin}` }],
      followUps: ['Show his GitHub', 'How else can I contact him?'],
    }),
  },
  {
    id: 'github',
    keywords: ['github', 'repo', 'repository', 'repositories', 'source', 'code'],
    phrases: ['source code', 'git hub'],
    answer: () => ({
      text: `${K.profile.firstName}'s GitHub: **${K.contactInfo.github}** — 30+ repositories spanning full-stack apps, AI/ML models and his open-source tools Baton and Elmory.`,
      links: [{ label: 'Open GitHub', url: `https://${K.contactInfo.github}` }, { label: 'Baton repo', url: K.baton.links[1].url }, { label: 'Elmory repo', url: K.elmory.links[1].url }],
      followUps: ['What is Elmory?', 'Tell me about Baton'],
    }),
  },
  {
    id: 'resume',
    keywords: ['resume', 'cv', 'curriculum', 'pdf', 'download'],
    weight: 1.3,
    answer: () => ({
      text: `You can download ${K.profile.firstName}'s resume as a PDF below. It summarises his experience at Foxwel.ai and Anave Enterprises, his AI/ML internships, education and key projects.`,
      links: [{ label: 'Download resume (PDF)', url: K.contactInfo.resume }, { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` }],
      followUps: ['Tell me about his experience', 'Is he available for hire?'],
    }),
  },
  {
    id: 'portfolio',
    keywords: ['portfolio', 'website', 'site'],
    phrases: ['this site', 'this website', 'built this'],
    weight: 0.9,
    answer: () => ({
      text: `This portfolio is itself one of ${K.profile.firstName}'s projects — React, Vite, Tailwind CSS and Framer Motion, with a WebGL hero, smooth scrolling and this assistant. Live at **${K.contactInfo.portfolio}**.`,
      links: [{ label: 'Portfolio source', url: 'https://github.com/Rakshan001/Portfolio-Website' }],
      followUps: ['What other projects has he built?', 'What is his tech stack?'],
    }),
  },
  {
    id: 'experience',
    keywords: ['experience', 'background', 'career', 'journey', 'history', 'worked', 'working', 'job', 'jobs', 'role', 'roles', 'company', 'companies', 'employer', 'timeline', 'professional'],
    phrases: ['work experience', 'where does he work', 'where do you work', 'current role', 'current company', 'work history'],
    answer: () => ({
      text: `**Professional experience (${K.profile.yearsExperience} years)**\n${list(K.experience.map((e) => `**${e.role}** · ${e.company} · ${e.period}`))}\n\nPlus: ${K.leadership.role} at the ${K.leadership.org} (${K.leadership.period}).\n\nAsk about any company for details — e.g. "What did he do at Anave?"`,
      links: [{ label: 'Download resume', url: K.contactInfo.resume }],
      followUps: ['What does he do at Foxwel.ai?', 'What did he build at Anave?', 'Tell me about his internships'],
    }),
  },
  {
    id: 'foxwel',
    keywords: ['foxwel', 'foxwell', 'foxvil', 'current', 'present', 'senior', 'gemini', 'drive', 'orders', 'inventory', 'platform', 'operations'],
    phrases: ['current job', 'right now', 'these days', 'at foxwel', 'currently', 'work now'],
    weight: 1.3,
    answer: () => {
      const e = K.experience[0];
      return {
        text: `**${e.role} · ${e.company}** (${e.period}, ${e.location})\n\n${e.summary}\n\n${list(e.bullets)}\n\n**Stack:** ${e.tech.join(', ')}`,
        links: [{ label: 'foxwel.ai', url: e.website }],
        followUps: ['What did he build at Anave?', 'What is his tech stack?'],
      };
    },
  },
  {
    id: 'anave',
    keywords: ['anave', 'anaway', 'hobbyistdecals', 'hobbyist', 'decals', 'decal', 'skyskale', 'skyscale', 'ecommerce', 'e-commerce', 'marketplace', 'shop', 'store', 'previous', 'last'],
    phrases: ['previous company', 'last company', 'before foxwel', 'hobbyist decals'],
    weight: 1.3,
    answer: () => {
      const e = K.experience[1];
      return {
        text: `**${e.role} · ${e.company}** (${e.period}, ${e.location})\n\n${e.summary}\n\n**What he built**\n${list(e.bullets)}\n\n**Stack:** ${e.tech.join(', ')}`,
        links: [...e.products.map((p) => ({ label: p.name, url: p.url })), { label: 'Anave Enterprises', url: e.website }],
        followUps: ['What does he do at Foxwel.ai?', 'Tell me about Baton'],
      };
    },
  },
  {
    id: 'internships',
    keywords: ['intern', 'internship', 'internships', 'accolade', 'entertainment', 'technologists', 'trainee'],
    answer: () => ({
      text: `**Internships**\n${K.experience.slice(2).map((e) => `**${e.role} · ${e.company}** (${e.period}, ${e.location})\n${e.summary}\n${list(e.bullets)}`).join('\n\n')}`,
      followUps: ['Tell me about his AI/ML work', 'What is his full experience?'],
    }),
  },
  {
    id: 'skills',
    keywords: ['skill', 'skills', 'stack', 'technologies', 'technology', 'tech', 'tools', 'languages', 'frameworks', 'expertise', 'proficient', 'good'],
    phrases: ['tech stack', 'what can he do', 'good at', 'programming languages'],
    answer: () => ({
      text: `**Technical expertise**\n${Object.entries(K.skills).map(([cat, items]) => `**${cat}:** ${items.join(', ')}`).join('\n')}\n\nAsk about any one — e.g. "has he used Docker?" — and I'll show where it was applied.`,
      followUps: ['Does he know Next.js?', 'Tell me about his AI/ML work', 'What projects has he built?'],
    }),
  },
  {
    id: 'frontend',
    keywords: ['frontend', 'front-end', 'ui', 'css', 'tailwind', 'design', 'responsive', 'animation'],
    phrases: ['front end', 'user interface'],
    weight: 1.1,
    answer: () => ({
      text: `**Frontend**: ${K.skills.Frontend.join(', ')}.\n\nHe works on React and Next.js products at Foxwel.ai, led the React front end for HobbyistDecals at Anave, built Baton's React dashboard and this animated portfolio. Comfortable with design systems, accessibility and performance (page load cut from 5s to 0.5s at Anave).`,
      followUps: ['What about backend?', 'Does he know TypeScript?'],
    }),
  },
  {
    id: 'backend',
    keywords: ['backend', 'back-end', 'api', 'apis', 'server', 'microservices', 'architecture', 'nestjs', 'node', 'nodejs', 'express', 'django', 'flask'],
    phrases: ['back end', 'rest api', 'system design'],
    weight: 1.1,
    answer: () => ({
      text: `**Backend**: ${K.skills.Backend.join(', ')}.\n\nAt Anave he designed secure REST APIs in Node.js and Express for order tracking, split payments, affiliate marketing and loyalty points. At Foxwel.ai he defines the data model and APIs for a multi-module business platform. He also built Baton's backend in Node.js and TypeScript. Earlier work used Django, Flask and PHP with MySQL.`,
      followUps: ['Which databases does he use?', 'Tell me about Baton'],
    }),
  },
  {
    id: 'database',
    keywords: ['database', 'databases', 'db', 'sql', 'postgres', 'postgresql', 'mysql', 'mongodb', 'mongo', 'sqlite', 'nosql'],
    weight: 1.1,
    answer: () => ({
      text: `**Databases**: ${K.skills.Databases.join(', ')}.\n\nHe designs schemas, optimises queries and indexes, and has cut load times 40% (Anave) and latency 50% (alumni portal) through query optimisation and caching.`,
      followUps: ['What about backend?', 'What is his full tech stack?'],
    }),
  },
  {
    id: 'aiml',
    keywords: ['ml', 'machine', 'learning', 'deep', 'neural', 'tensorflow', 'opencv', 'vision', 'nlp', 'rag', 'langchain', 'langgraph', 'langsmith', 'chatbot', 'llm', 'llms', 'genai', 'artificial', 'intelligence', 'models', 'agents', 'orchestration', 'embeddings', 'prompt'],
    phrases: ['machine learning', 'artificial intelligence', 'computer vision', 'ai work', 'ai ml', 'ai/ml'],
    answer: () => ({
      text: `**GenAI & LLMs**: ${K.skills['GenAI & LLMs'].join(', ')}.\n**Machine learning**: ${K.skills['AI / ML'].join(', ')}.\n\n${list([
        'Production: Gemini API in a business operations platform at Foxwel.ai; RAG chatbot with LangChain and LangSmith at Anave',
        'Open source: Baton orchestrates 6 AI coding agents on one repo with plan handoff between them',
        'Research: Music Genre Classification (CNN, 94% accuracy), Brain Stroke Prediction (5-model ensemble on CT scans), Blind Voting System (speech + NLP)',
        'Two AI/ML internships with 95% model accuracy and a 70% performance gain',
      ])}`,
      followUps: ['Tell me about the stroke prediction project', 'What is Baton?'],
    }),
  },
  {
    id: 'devops',
    keywords: ['devops', 'docker', 'aws', 'cloud', 'deploy', 'deployment', 'cicd', 'ci/cd', 'pipeline', 'kubernetes', 'vercel', 'netlify', 'hosting'],
    phrases: ['ci cd'],
    weight: 1.1,
    answer: () => ({
      text: `**DevOps & cloud**: ${K.skills['DevOps & Cloud'].join(', ')}.\n\nHe set up GitHub Actions CI/CD that cut deployment time 60% at Anave, deployed the alumni portal on AWS EC2 with GitHub Actions, and ships Baton's docs and this portfolio on Vercel and Netlify.`,
      followUps: ['Does he know Docker?', 'What is his full tech stack?'],
    }),
  },
  {
    id: 'elmory',
    keywords: ['elmory', 'elmery', 'elmorry', 'memory', 'stale', 'evidence', 'quote', 'quotes', 'recall', 'abstain', 'remember', 'forget', 'supersedes'],
    phrases: ['agent memory', 'memory for agents', 'evidence checked', 'evidence-checked', 'out of date', 'i dont know'],
    weight: 1.3,
    answer: () => ({
      text: `**Elmory — open-source memory for AI coding agents (MIT)**\n${K.elmory.oneLiner}\n\n**The problem:** ${K.elmory.problem}\n\n**How it works**\n${list(K.elmory.how)}\n\n**Interfaces:** ${K.elmory.interfaces}\n\n**Tech:** ${K.elmory.tech}\n\n**Status:** ${K.elmory.status}`,
      links: K.elmory.links,
      followUps: ['Tell me about Baton', 'What is his tech stack?'],
    }),
  },
  {
    id: 'opensource',
    keywords: ['opensource', 'oss', 'contributions', 'contribute', 'contributor', 'maintainer', 'mit'],
    phrases: ['open source', 'open-source', 'side projects', 'developer tools'],
    weight: 1.25,
    answer: () => ({
      text: `${K.profile.firstName} builds and maintains two open-source developer tools for AI coding agents, both MIT-licensed:\n${list([
        `**Baton** — ${K.baton.oneLiner}`,
        `**Elmory** — ${K.elmory.oneLiner}`,
      ])}\n\nBaton coordinates the agents; Elmory gives them memory that can't silently go stale.`,
      links: [...K.baton.links, ...K.elmory.links],
      followUps: ['What is Elmory?', 'Tell me about Baton'],
    }),
  },
  {
    id: 'baton',
    keywords: ['baton', 'multi-agent', 'agents', 'orchestration', 'orchestrate', 'harness', 'worktree', 'worktrees', 'handoff', 'daemon', 'mcp', 'codex', 'tokens'],
    phrases: ['multi agent', 'coding agents', 'agent orchestration', 'team mode', 'one click setup', 'knowledge base', 'build baton', 'built baton', 'create baton', 'created baton', 'make baton', 'made baton'],
    weight: 1.2,
    answer: ({ norm }) => (BATON_INTERNALS.test(norm) ? batonInternals() : {
      text: `**Baton — flagship open-source project (MIT)**\n${K.baton.oneLiner}\n\n**Why he built it:** ${K.baton.story}\n\n**What it does**\n${list(K.baton.features)}\n\n**Result:** ${K.baton.results}\n\n${K.baton.scale}`,
      links: K.baton.links,
      followUps: ['How does he use AI to ship faster?', 'What is Elmory?', 'Why should we hire him?'],
    }),
  },
  {
    id: 'projects',
    keywords: ['project', 'projects', 'built', 'build', 'made', 'created', 'apps', 'applications', 'work', 'works', 'showcase', 'demo'],
    phrases: ['what has he built', 'what have you built', 'show me projects'],
    answer: ({ projects }) => {
      const featured = projects.filter((p) => !['Baton', 'Elmory'].includes(p.title)).slice(0, 7);
      return {
        text: `**Projects (12+)** — a few highlights:\n${list([
          `**Baton** — orchestration for AI coding agents, 10 to 15 agents on one codebase, 641 tests`,
          `**Elmory** — open-source memory for coding agents that serves a fact only while the code it quotes still exists`,
          `**HobbyistDecals.com** — production MERN marketplace whose architecture he led at Anave`,
          ...featured.map((p) => `**${p.title}** — ${p.description.split('. ')[0].replace(/\.$/, '')}`),
        ])}\n\nAsk about any one by name for details and links.`,
        followUps: ['What is Elmory?', 'Tell me about Baton', 'Tell me about his ML projects'],
      };
    },
  },
  {
    id: 'education',
    keywords: ['education', 'educational', 'study', 'studied', 'degree', 'college', 'university', 'school', 'graduate', 'graduated', 'cgpa', 'gpa', 'marks', 'academic', 'qualification', 'engineering', 'canara', 'puc', 'sslc'],
    phrases: ['where did he study', 'educational background'],
    answer: () => ({
      text: `**Education**\n${list(K.education.map((e) => `**${e.degree}** · ${e.institution} · ${e.period} · ${e.score}`))}\n\n**Certifications:** ${K.certifications.join(', ')}`,
      followUps: ['What are his achievements?', 'Tell me about his experience'],
    }),
  },
  {
    id: 'certifications',
    keywords: ['certificate', 'certificates', 'certification', 'certifications', 'certified', 'course', 'courses', 'training', 'workshop'],
    weight: 1.2,
    answer: () => ({
      text: `**Certifications & training**\n${list(K.certifications)}\n\nHe also keeps current through workshops, hackathons and hands-on projects.`,
      followUps: ['What is his education?', 'What are his achievements?'],
    }),
  },
  {
    id: 'achievements',
    keywords: ['achievement', 'achievements', 'award', 'awards', 'accomplishment', 'accomplishments', 'won', 'winner', 'proud', 'highlights', 'impact', 'results', 'metrics'],
    answer: () => ({
      text: `**Key achievements**\n${list(K.achievements)}`,
      followUps: ['Tell me about Baton', 'What did he build at Anave?'],
    }),
  },
  {
    id: 'why-hire',
    keywords: ['why', 'strength', 'strengths', 'stand', 'different', 'unique', 'value', 'impress', 'convince', 'special', 'best'],
    phrases: ['why should', 'why hire', 'why him', 'what makes him', 'stand out', 'what can he bring', 'what does he bring', 'strongest', 'his strengths', 'worth hiring'],
    weight: 1.4,
    answer: () => ({
      text: `Here's the short version a hiring manager usually wants:\n${list([
        '**He ships whole products.** React and Next.js front ends, Node.js and Python back ends, PostgreSQL and MongoDB, Docker, AWS and CI/CD, so one person can carry a feature from schema to production.',
        '**He leads the architecture.** He designs the system and guides the team through delivery at Foxwel.ai, and led architecture and design for HobbyistDecals.com, a marketplace shipping to 42 countries.',
        '**GenAI in production, not just demos.** A RAG chatbot over a live catalog and order history (LangChain, LangSmith), and Gemini built into a business platform.',
        '**Measurable results.** Page load cut from 5s to 0.5s, deploys 60% faster with GitHub Actions, apps used by 500+ active users.',
        '**He builds his own tools.** Baton and Elmory are open-source developer tools for AI coding agents, with 641 tests on Baton alone. That says a lot about depth and initiative.',
      ])}\n\nIf that matches what you need, ${K.profile.firstName} would be glad to talk: **${K.contactInfo.email}**.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }, { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` }, { label: 'Download resume', url: K.contactInfo.resume }],
      followUps: ['Is he a good fit for a backend role?', 'How does he use AI to ship faster?', 'Can I set up a call?'],
    }),
  },
  {
    id: 'role-fit',
    keywords: ['fit', 'suitable', 'suit', 'candidate', 'role', 'position', 'qualified', 'consider', 'match'],
    phrases: ['good fit', 'right fit', 'fit for', 'suitable for', 'good for a', 'right person', 'can he handle', 'can he do', 'could he work as', 'work as a'],
    weight: 1.3,
    answer: ({ norm }) => roleFit(norm),
  },
  {
    id: 'seniority',
    keywords: ['seniority', 'junior', 'fresher', 'level', 'years'],
    phrases: ['how many years', 'years of experience', 'how experienced', 'experience level', 'is he senior', 'is he junior', 'how senior', 'mid level', 'mid-level'],
    weight: 1.4,
    answer: () => ({
      text: `About **2 years of production experience**, and he currently holds a **Senior Software Developer** title.\n\nWhat that looks like in practice:\n${list([
        'In both of his engineering roles he led the system architecture and design, not just individual tickets.',
        'He owns features from the data model and APIs through to release, and guides the team building them.',
        'Before that: two AI/ML internships (2024 to 2025) and leading the build of his college alumni portal.',
        'On the side he maintains two open-source projects, which is where a lot of his depth in TypeScript, Node.js and AI agents comes from.',
      ])}`,
      followUps: ['Why should we hire him?', 'Tell me about his experience', 'Is he a team player?'],
    }),
  },
  {
    id: 'teamwork',
    keywords: ['team', 'teamwork', 'communication', 'communicate', 'leadership', 'leader', 'mentor', 'mentoring', 'stakeholders', 'culture', 'attitude', 'personality', 'behaviour', 'behavior', 'player', 'manage', 'managing'],
    phrases: ['team player', 'work style', 'working style', 'soft skills', 'works with others', 'how does he work', 'work with a team', 'lead a team'],
    weight: 1.3,
    answer: () => ({
      text: `Yes, and he leads from inside the team rather than working alone:\n${list([
        "At Foxwel.ai he designs the architecture and guides the team from the data model and API contracts to each release.",
        'At Anave he led architecture and design for HobbyistDecals.com and worked with the team and the design studio on order intake, design approvals and customer messaging.',
        'He led the team that built his college alumni portal.',
        'He writes things down: Baton and Elmory both ship with documentation sites, which is a good sign for how he hands work over.',
        'He has worked remote (with a Los Angeles team), hybrid and on-site, and speaks English, Kannada, Hindi and Tulu.',
      ])}`,
      followUps: ['How experienced is he?', 'Can I set up a call?'],
    }),
  },
  {
    id: 'ai-workflow',
    keywords: ['productivity', 'faster', 'speed', 'copilot', 'vibe', 'workflow', 'efficient', 'efficiency', 'claude', 'cursor', 'windsurf'],
    phrases: ['ai tools', 'ai coding', 'use ai', 'uses ai', 'with ai', 'ship faster', 'ship products faster', 'claude code', 'ai assisted', 'ai-assisted', 'ai agents to code', 'build faster', 'deliver faster'],
    weight: 1.35,
    answer: () => ({
      text: `This is one of his specialities. He ships with several AI coding agents working in parallel, and builds the tooling that makes that safe:\n${list([
        '**Parallel agents.** Claude Code, Cursor, Codex and others each take a separate task, so more work moves at once.',
        '**Plan on a strong model, build on cheaper ones.** Claude Code plans and reviews while cheaper and free agents write the code, so the expensive tokens go to decisions.',
        '**No collisions.** His tool **Baton** runs 10 to 15 agents on one codebase, and an agent waits when another is already editing the same file.',
        "**Memory that can be trusted.** His tool **Elmory** stops agents acting on stale notes: a remembered fact is only used while the code it quotes still exists.",
      ])}\n\nFor a team adopting AI coding agents, that's experience that's hard to find.`,
      links: [...K.baton.links.slice(0, 1), ...K.elmory.links.slice(0, 1)],
      followUps: ['Tell me about Baton', 'What is Elmory?', 'Why should we hire him?'],
    }),
  },
  {
    id: 'goals',
    keywords: ['goal', 'goals', 'ambition', 'motivation', 'motivated', 'passion', 'passionate', 'aspire', 'aspiration', 'ideal', 'interested', 'interests'],
    phrases: ['what kind of role', 'ideal role', 'next role', 'career goals', 'what does he want', 'interested in', 'where does he see', 'looking for', 'kind of company', 'what motivates'],
    weight: 1.3,
    answer: () => ({
      text: `${K.profile.availability}\n\nWhat gets him most excited:\n${list([
        'Owning a product area end to end, from architecture to release.',
        'GenAI products that go beyond demos: RAG, agents and LLM features that real users rely on.',
        'Developer tooling, especially making AI coding agents faster and safer to use.',
      ])}\n\nIf your role sits anywhere near that, he'd love to hear about it: **${K.contactInfo.email}**.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }, { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` }],
      followUps: ['Is he a good fit for a GenAI role?', 'Where is he located?'],
    }),
  },
  {
    id: 'growth',
    keywords: ['weakness', 'weaknesses', 'weak', 'gap', 'gaps', 'lack', 'lacks', 'improve', 'improvement', 'shortcoming', 'shortcomings'],
    phrases: ['areas to improve', 'bad at', 'not good at', 'what is he learning', 'room to grow', 'still learning'],
    weight: 1.3,
    answer: () => ({
      text: `A fair question, so here's an honest answer:\n${list([
        "**Large-scale infrastructure** such as Kubernetes, Terraform or a dedicated SRE role isn't in his stack yet. He deploys with Docker, AWS and GitHub Actions, which covers most product teams.",
        '**Years on the clock.** He has about 2 years of production experience, fewer than a staff engineer. He makes up for it with ownership: leading architecture in both roles and maintaining two open-source projects.',
      ])}\n\nHe picks up adjacent tools quickly, and is happy to talk through any gap that matters for your role.`,
      followUps: ['Why should we hire him?', 'What is his full tech stack?'],
    }),
  },
  {
    id: 'interview',
    keywords: ['interview', 'meeting', 'schedule', 'meet', 'speak', 'discuss', 'zoom', 'calendar', 'chat'],
    phrases: ['set up a call', 'schedule a call', 'schedule an interview', 'talk to him', 'speak to him', 'speak with him', 'get on a call', 'book a call', 'can we talk', 'have a call', 'quick call'],
    weight: 1.35,
    answer: () => ({
      text: `He'd be glad to. The easiest way:\n${list([
        `**Email** ${K.contactInfo.email} with the role, the team and a few times that suit you.`,
        `**Phone or WhatsApp:** ${K.contactInfo.phone}`,
        `**LinkedIn:** ${K.contactInfo.linkedin}`,
      ])}\n\nHis resume is below if you'd like it before the call.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }, { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` }, { label: 'Download resume', url: K.contactInfo.resume }],
      followUps: ['Why should we hire him?', 'Where is he located?'],
    }),
  },
  {
    id: 'availability',
    keywords: ['hire', 'hiring', 'available', 'availability', 'opportunity', 'opportunities', 'open', 'freelance', 'contract', 'consulting', 'collaborate', 'collaboration', 'recruit', 'join', 'position', 'vacancy', 'offer', 'notice'],
    phrases: ['open to work', 'for hire', 'available for', 'notice period', 'join us', 'work with us', 'looking for a job', 'looking for work', 'looking for opportunities'],
    answer: () => ({
      text: `**Yes.** ${K.profile.availability}\n\nHe's currently a ${K.profile.title} at ${K.profile.company}. For roles, freelance work or collaborations, the quickest route is email: **${K.contactInfo.email}**. Notice period and start dates are best discussed directly.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }, { label: 'LinkedIn', url: `https://${K.contactInfo.linkedin}` }, { label: 'Download resume', url: K.contactInfo.resume }],
      followUps: ['Where is he located?', 'What is his tech stack?'],
    }),
  },
  {
    id: 'salary',
    keywords: ['salary', 'compensation', 'package', 'ctc', 'pay', 'charge', 'charges', 'price', 'budget', 'lpa', 'expectation'],
    phrases: ['how much', 'hourly rate', 'freelance rate', 'your rates', 'his rates'],
    weight: 1.2,
    answer: () => ({
      text: `Compensation and freelance rates depend on the role, scope and company, so ${K.profile.firstName} prefers to discuss them directly. Please email **${K.contactInfo.email}** with the details and he'll get back to you.`,
      links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
    }),
  },
  {
    id: 'location',
    keywords: ['location', 'located', 'live', 'lives', 'living', 'based', 'where', 'city', 'country', 'india', 'karnataka', 'bantwal', 'mangalore', 'bengaluru', 'bangalore', 'remote', 'relocate', 'relocation', 'onsite', 'hybrid', 'timezone'],
    phrases: ['where is he', 'where does he live', 'work remotely', 'time zone'],
    answer: () => ({
      text: `**Location:** ${K.profile.location}.\n\n**Work mode:** ${K.profile.workMode} He has worked fully remote (Entertainment Technologists), hybrid (Foxwel.ai) and on-site, and is open to relocation for the right opportunity.`,
      followUps: ['Is he available for hire?', 'How can I contact him?'],
    }),
  },
  {
    id: 'languages',
    keywords: ['speak', 'speaks', 'spoken', 'english', 'kannada', 'hindi', 'tulu', 'communication', 'fluent'],
    phrases: ['languages does he speak', 'spoken languages'],
    weight: 1.1,
    answer: () => ({
      text: `${K.profile.firstName} speaks:\n${list(K.spokenLanguages)}\n\nAll of his professional work, documentation and open-source projects are in English.`,
      followUps: ['Where is he located?', 'Is he available for hire?'],
    }),
  },
  {
    id: 'hobbies',
    keywords: ['hobby', 'hobbies', 'interests', 'passion', 'enjoy'],
    phrases: ['free time', 'outside work'],
    weight: 0.9,
    answer: () => ({
      text: `Outside of client work, ${K.profile.firstName} builds open-source tooling (Baton grew out of running several AI coding agents on one repo), explores new AI models and frameworks, and takes part in hackathons and workshops.`,
      followUps: ['Tell me about Baton', 'What are his achievements?'],
    }),
  },
];

// ---------- project & technology lookup ----------
const projectAliases = {
  'nexus alumni portal': ['alumni', 'nexus', 'alumni portal'],
  nexttrip: ['nexttrip', 'bus', 'buses', 'trip'],
  'e-commerce website': ['php ecommerce', 'ecommerce website'],
  'doctor appointment system': ['doctor', 'appointment', 'clinic', 'hospital'],
  'portfolio website': [],
  'sgpa & cgpa calculator': ['sgpa', 'cgpa calculator', 'calculator'],
  'music genre classification': ['music', 'genre', 'song', 'audio'],
  'blind voting system (applied ai)': ['blind', 'voting', 'vote', 'accessibility'],
  'brain stroke prediction system': ['stroke', 'brain', 'medical', 'ct scan'],
};

const findProject = (norm, toks, projects) => {
  let best = null, bestScore = 0;
  for (const p of projects) {
    if (['Baton', 'Elmory', 'Portfolio Website'].includes(p.title)) continue;
    const title = normalize(p.title);
    const aliases = projectAliases[title] || [];
    let score = 0;
    if (norm.includes(title)) score += 6;
    for (const a of aliases) {
      if (a.includes(' ')) { if (norm.includes(a)) score += 4; }
      else if (toks.some((t) => fuzzyEq(t, a))) score += 3;
    }
    for (const w of tokens(title)) if (w.length >= 4 && !STOP.has(w) && toks.some((t) => fuzzyEq(t, w))) score += 2;
    if (score > bestScore) { best = p; bestScore = score; }
  }
  return bestScore >= 3 ? best : null;
};

const projectAnswer = (p) => {
  const links = [];
  if (p.liveUrl) links.push({ label: 'Live demo', url: p.liveUrl });
  if (p.githubUrl) links.push({ label: 'GitHub repo', url: p.githubUrl });
  return {
    text: `**${p.title}** · ${p.category}\n${p.description}${p.features.length ? `\n\n**Highlights**\n${list(p.features.map((f) => f.split(' — ')[0]))}` : ''}\n\n**Built with:** ${p.technologies.join(', ')}`,
    links,
    followUps: ['What other projects has he built?', 'What is his tech stack?'],
    intent: 'project',
  };
};

const techIndex = (projects) => {
  const map = new Map(); // normalized tech → { name, projects:Set, roles:Set }
  const add = (name, where, kind) => {
    const key = normalize(name).replace(/\.js$/, '').replace(/js$/, '');
    if (!key) return;
    const entry = map.get(key) || { name, projects: new Set(), roles: new Set() };
    (kind === 'project' ? entry.projects : entry.roles).add(where);
    map.set(key, entry);
  };
  for (const p of projects) for (const t of p.technologies) add(t, p.title, 'project');
  for (const e of K.experience) for (const t of e.tech) add(t, e.company, 'role');
  for (const items of Object.values(K.skills)) for (const t of items) add(t, null, 'role');
  return map;
};

const TECH_QUESTION = /\b(know|knows|use|used|uses|using|familiar|experience|experienced|worked|work|comfortable|proficient|skilled|good|tried|built)\b/;

/** Edit distance that counts a swapped pair of letters as one edit ("dokcer" → "docker"). */
const typoDistance = (a, b) => {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 99;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[m][n];
};
const allowedTypos = (len) => (len >= 8 ? 2 : len >= 5 ? 1 : 0);
const techKey = (name) => normalize(name).replace(/\.js$/, '').replace(/js$/, '');

/**
 * Well-known technologies that are NOT in his stack, so a question about one (even misspelt)
 * gets an honest "no" plus the closest tools he does use, instead of a guess.
 */
const TECH_CATALOG = [
  { name: 'Kubernetes', aliases: ['kubernetes', 'k8s', 'kubectl', 'eks', 'aks', 'gke'], related: ['Docker', 'AWS', 'GitHub Actions'] },
  { name: 'Terraform', aliases: ['terraform', 'opentofu'], related: ['AWS', 'Docker', 'GitHub Actions'] },
  { name: 'Ansible', aliases: ['ansible'], related: ['Docker', 'GitHub Actions'] },
  { name: 'Jenkins', aliases: ['jenkins'], related: ['GitHub Actions', 'CI/CD', 'Docker'] },
  { name: 'Google Cloud', aliases: ['gcp', 'google cloud', 'firebase hosting'], related: ['AWS', 'Vercel'] },
  { name: 'Azure', aliases: ['azure'], related: ['AWS', 'Vercel'] },
  { name: 'GraphQL', aliases: ['graphql', 'apollo'], related: ['REST APIs', 'Node.js', 'Express'] },
  { name: 'gRPC', aliases: ['grpc', 'protobuf'], related: ['REST APIs', 'Node.js'] },
  { name: 'Kafka', aliases: ['kafka', 'apache kafka'], related: ['Redis', 'Server-Sent Events', 'Node.js'] },
  { name: 'RabbitMQ', aliases: ['rabbitmq', 'rabbit mq'], related: ['Redis', 'Node.js'] },
  { name: 'Elasticsearch', aliases: ['elasticsearch', 'elastic search', 'opensearch'], related: ['vector search', 'PostgreSQL', 'SQLite full-text search'] },
  { name: 'Go', aliases: ['golang', 'go lang'], related: ['TypeScript', 'Node.js', 'Python'] },
  { name: 'Rust', aliases: ['rust', 'rustlang'], related: ['TypeScript', 'Python'] },
  { name: 'C++', aliases: ['c++', 'cpp'], related: ['Java', 'Python'] },
  { name: 'C#/.NET', aliases: ['c#', 'csharp', 'dotnet', '.net', 'asp.net'], related: ['Java', 'Node.js', 'TypeScript'] },
  { name: 'Spring Boot', aliases: ['spring boot', 'springboot', 'spring'], related: ['Java', 'Node.js', 'Express'] },
  { name: 'Angular', aliases: ['angular', 'angularjs'], related: ['React', 'Next.js', 'TypeScript'] },
  { name: 'Vue', aliases: ['vue', 'vuejs', 'vue.js', 'nuxt'], related: ['React', 'Next.js'] },
  { name: 'Svelte', aliases: ['svelte', 'sveltekit'], related: ['React', 'Next.js'] },
  { name: 'Swift', aliases: ['swift', 'swiftui'], related: ['React Native'] },
  { name: 'Kotlin', aliases: ['kotlin'], related: ['React Native', 'Java'] },
  { name: 'Firebase', aliases: ['firebase', 'firestore'], related: ['MongoDB', 'PostgreSQL', 'AWS'] },
  { name: 'Supabase', aliases: ['supabase'], related: ['PostgreSQL'] },
  { name: 'Prisma', aliases: ['prisma'], related: ['PostgreSQL', 'MongoDB'] },
  { name: 'Pinecone', aliases: ['pinecone', 'weaviate', 'qdrant', 'chroma', 'chromadb'], related: ['vector search', 'embeddings', 'RAG'] },
  { name: 'LlamaIndex', aliases: ['llamaindex', 'llama index', 'llama-index'], related: ['LangChain', 'RAG'] },
  { name: 'Hugging Face', aliases: ['huggingface', 'hugging face'], related: ['TensorFlow', 'OpenAI API', 'Gemini API'] },
  { name: 'Spark', aliases: ['pyspark', 'apache spark', 'spark'], related: ['Python', 'SQL'] },
  { name: 'Airflow', aliases: ['airflow'], related: ['Python', 'GitHub Actions'] },
  { name: 'FastAPI', aliases: ['fastapi', 'fast api'], related: ['Flask', 'Django', 'Express'] },
  { name: 'Jest', aliases: ['jest'], related: ['Vitest'] },
  { name: 'Selenium', aliases: ['selenium', 'cypress'], related: ['Vitest'] },
];

/** Everything the resolver can recognise: his stack first, then the catalogue, hashed for O(1) exact hits. */
const buildTechVocab = (index) => {
  const list = [];
  for (const [key, entry] of index) list.push({ key, kind: 'stack', entry });
  for (const c of TECH_CATALOG) for (const alias of c.aliases) list.push({ key: techKey(alias), kind: 'catalog', entry: c, alias: true });
  const exact = new Map();
  const byLen = new Map();
  for (const v of list) {
    if (!exact.has(v.key)) exact.set(v.key, []);
    exact.get(v.key).push(v);
    if (!byLen.has(v.key.length)) byLen.set(v.key.length, []);
    byLen.get(v.key.length).push(v);
  }
  return { exact, byLen };
};

/**
 * Find the technology a question is about, tolerating typos and split words
 * ("kubernetis", "dokcer", "next js", "lang chain"). Exact names and aliases are a
 * hash lookup; only unmatched words are compared, and only against names of a
 * similar length.
 */
const resolveTech = (norm, toks, vocab) => {
  if (!TECH_QUESTION.test(norm) && toks.length > 3) return null;
  const words = tokens(norm).filter((t) => !STOP.has(t) && !TECH_QUESTION.test(t));
  const grams = [];
  words.forEach((w, i) => {
    if (words[i + 1]) grams.push(`${w} ${words[i + 1]}`, `${w}${words[i + 1]}`);
    grams.push(w);
  });
  const rank = (v, dist) => dist * 10 - v.key.length * 0.1 + (v.kind === 'catalog' ? 0.5 : 0);
  let best = null;
  for (const raw of grams) {
    const g = techKey(raw);
    if (!g) continue;
    for (const v of vocab.exact.get(g) || []) {
      const score = rank(v, 0);
      if (!best || score < best.score) best = { ...v, dist: 0, typed: raw, score };
    }
  }
  if (best) return best;
  for (const raw of grams) {
    const g = techKey(raw);
    if (!g || g.length < 4) continue;
    for (let len = g.length - 2; len <= g.length + 2; len++) {
      for (const v of vocab.byLen.get(len) || []) {
        if (v.key.length < 4) continue;
        const dist = typoDistance(g, v.key);
        if (dist > allowedTypos(Math.min(g.length, v.key.length))) continue;
        const score = rank(v, dist);
        if (!best || score < best.score) best = { ...v, dist, typed: raw, score };
      }
    }
  }
  return best;
};

const notInStackAnswer = (c, typed, dist) => ({
  text: `**${c.name} isn't in ${K.profile.firstName}'s stack**, so I won't claim it.${correction(typed, c.name, dist)}\n\nThe closest tools he works with are **${c.related.join(', ')}**. He picks up adjacent tools quickly, so if ${c.name} matters for your role, ask him at **${K.contactInfo.email}**.`,
  links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
  followUps: ['What is his full tech stack?', 'Is he available for hire?'],
  intent: 'tech-unknown',
});

const correction = (typed, name, dist) => (dist > 0 ? ` (I read "${typed}" as ${name}.)` : '');

const techAnswer = (t, typed, dist) => {
  const roles = [...t.roles].filter(Boolean);
  const projects = [...t.projects];
  const where = [];
  if (roles.length) where.push(`professionally at ${roles.join(' and ')}`);
  if (projects.length) where.push(`in projects such as ${projects.slice(0, 3).join(', ')}`);
  return {
    text: `**Yes — ${t.name}.**${correction(typed, t.name, dist)} ${K.profile.firstName} has used it ${where.length ? where.join(', and ') : 'as part of his core stack'}.\n\nWant the full stack, or details on one of those projects?`,
    followUps: ['What is his full tech stack?', ...(projects[0] ? [`Tell me about ${projects[0]}`] : [])],
    intent: 'tech',
  };
};

// ---------- follow-ups & fallback ----------
const FOLLOW_UP = /^(tell me )?(more|details|elaborate|explain|go on|continue|why|how|and|ok|okay|sure|yes|yeah|yep|please)\b/;
const MORE = {
  elmory: 'baton', opensource: 'elmory',
  experience: 'anave', foxwel: 'anave', anave: 'foxwel', skills: 'aiml', projects: 'baton', baton: 'projects',
  greeting: 'about', about: 'experience', education: 'certifications', aiml: 'projects', contact: 'availability',
  availability: 'contact', internships: 'experience', achievements: 'baton', frontend: 'backend', backend: 'database',
};

const fallback = (ctx) => ({
  text: `I don't have that in my notes — I only cover ${K.profile.firstName}'s professional profile. Here's what I can help with:\n${list([
    'His **experience** at Foxwel.ai and Anave Enterprises',
    'His **projects** (try "Tell me about Baton")',
    'Any **technology** ("Does he know Docker?")',
    '**Education**, **availability** or **contact** details',
  ])}\n\nFor anything else, email him at **${K.contactInfo.email}**.`,
  links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
  followUps: ['Tell me about his experience', 'What projects has he built?', 'How can I contact him?'],
  intent: 'fallback',
  unknown: ctx?.message,
});

// ---------- retrieval passages (knowledge → searchable chunks) ----------
const buildPassages = (projects) => {
  const out = [];
  const add = (title, text, meta = {}) => out.push({ title, text, ...meta });
  add('Profile', `${K.profile.summary} ${K.profile.availability} ${K.profile.workMode} Location ${K.profile.location}.`, { intent: 'about', tags: ['summary', 'bio', 'overview'] });
  add('Availability', `${K.profile.availability} Email ${K.contactInfo.email}.`, { intent: 'availability', tags: ['hire', 'available', 'freelance', 'opportunity', 'notice period'] });
  add('Contact', `Email ${K.contactInfo.email}. Phone ${K.contactInfo.phone}. LinkedIn ${K.contactInfo.linkedin}. GitHub ${K.contactInfo.github}. Resume PDF available.`, { intent: 'contact', tags: ['reach', 'connect'] });
  for (const e of K.experience) {
    add(`${e.role} at ${e.company}`, `${e.period}. ${e.location}. ${e.summary}`, { intent: e.company.startsWith('Foxwel') ? 'foxwel' : e.company.startsWith('Anave') ? 'anave' : 'internships', tags: ['experience', 'role', 'company', 'work'] });
    for (const bullet of e.bullets) add(`${e.company}`, bullet, { intent: e.company.startsWith('Foxwel') ? 'foxwel' : e.company.startsWith('Anave') ? 'anave' : 'internships', tags: ['experience', ...e.tech] });
  }
  add(K.leadership.org, `${K.leadership.role}, ${K.leadership.period}. ${K.leadership.summary}`, { intent: 'experience', tags: ['leadership', 'lead', 'alumni', 'club'] });
  for (const ed of K.education) add(ed.degree, `${ed.institution}, ${ed.period}, ${ed.score}.`, { intent: 'education', tags: ['education', 'college', 'score', 'degree'] });
  add('Certifications', K.certifications.join('. '), { intent: 'certifications', tags: ['certification', 'course', 'training'] });
  for (const a of K.achievements) add('Achievement', a, { intent: 'achievements', tags: ['achievement', 'award', 'impact'] });
  for (const [cat, items] of Object.entries(K.skills)) add(`${cat} skills`, items.join(', '), { intent: 'skills', tags: ['skill', 'stack', 'technology', cat] });
  add('Baton', `${K.baton.oneLiner} ${K.baton.story} ${K.baton.features.join(' ')} ${K.baton.results} ${K.baton.scale}`, { intent: 'baton', tags: ['open source', 'project', 'agents', 'orchestration', 'tokens'] });
  add('Elmory', `${K.elmory.oneLiner} ${K.elmory.problem} ${K.elmory.how.join(' ')} ${K.elmory.interfaces} ${K.elmory.tech} ${K.elmory.status}`, { intent: 'elmory', tags: ['open source', 'project', 'memory', 'agents', 'evidence', 'stale'] });
  for (const p of projects.filter((x) => !['Baton', 'Elmory', 'Portfolio Website'].includes(x.title))) add(p.title, `${p.category}. ${p.subtitle} ${p.description} ${p.features.join(' ')} Built with ${p.technologies.join(', ')}.`, { intent: 'project', project: p, tags: ['project'] });
  return out;
};

/** Deterministic small hash → stable variation per (question, turn). */
const hashStr = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
const pick = (arr, seed) => arr[seed % arr.length];
const NUDGES = [
  `If you're hiring for something like this, ${K.profile.firstName} would be glad to talk. The quickest way is email: **${K.contactInfo.email}**.`,
  `Sounds relevant to a role you're filling? Drop ${K.profile.firstName} a line at **${K.contactInfo.email}**. He's happy to walk you through any of this in person.`,
  `Want to go deeper than a chatbot can? ${K.profile.firstName} is easy to reach at **${K.contactInfo.email}**, and happy to set up a call.`,
];

const OPENERS = {
  fresh: ['', '', 'Sure — ', 'Happy to help. ', 'Good question. ', 'Here you go. '],
  repeat: ['As I mentioned earlier — ', 'Quick recap, since we touched on this: ', 'Here it is again: ', 'Same as before — '],
  retrieved: ['Here\'s what I found: ', 'From his profile: ', 'The closest match I have: ', 'This should cover it: '],
};

// ---------- public API ----------
export function createAssistant({ projects = [] } = {}) {
  const tIndex = techIndex(projects);
  const techVocab = buildTechVocab(tIndex);
  const byId = Object.fromEntries(INTENTS.map((i) => [i.id, i]));
  const compiled = compileIntents(INTENTS);
  const run = (intent, norm = '') => ({ intent: intent.id, ...intent.answer({ projects, norm }) });
  const index = buildIndex(buildPassages(projects));
  const memo = new Map(); // normalized question → raw answer (hash-map cache for repeats)

  /** Retrieval answer: stitch the top passages when no intent is confident. */
  const retrieve = (message) => {
    const hits = index.search(message, 3).filter((h) => h.score > 2.2);
    if (!hits.length) return null;
    const top = hits[0];
    if (top.project) return projectAnswer(top.project);
    // If the best passage clearly belongs to one intent, give that intent's full answer.
    if (top.intent && byId[top.intent] && (hits.length === 1 || hits[1].intent === top.intent || top.score > hits[1].score * 1.5)) {
      return run(byId[top.intent]);
    }
    const unique = hits.filter((h, i, arr) => arr.findIndex((x) => x.title === h.title && x.text === h.text) === i).slice(0, 2);
    return {
      text: unique.map((h) => `**${h.title}** — ${h.text}`).join('\n\n'),
      followUps: [...new Set(unique.map((h) => h.intent).filter((i) => i && byId[i]))].slice(0, 2).map((i) => `Tell me more about ${i === 'foxwel' ? 'Foxwel.ai' : i === 'anave' ? 'Anave' : i}`),
      intent: 'retrieved',
      retrieved: true,
    };
  };

  const NO_OPENER = ['greeting', 'thanks', 'bye', 'fallback', 'tech', 'tech-unknown', 'interview', 'why-hire'];
  const NO_NUDGE = ['contact', 'interview', 'thanks', 'bye', 'greeting', 'salary', 'availability', 'linkedin', 'resume', 'why-hire', 'goals'];

  const decorate = (raw, message, context) => {
    const seed = hashStr(`${normalize(message)}|${context.turn || 0}`);
    const seen = new Set(context.seenIntents || []);
    let opener = '';
    if (raw.noOpener) opener = '';
    else if (raw.retrieved) opener = pick(OPENERS.retrieved, seed);
    else if (raw.intent && seen.has(raw.intent) && !['greeting', 'thanks', 'bye', 'fallback', 'help', 'bot-identity', 'project', 'tech', 'tech-unknown'].includes(raw.intent)) opener = pick(OPENERS.repeat, seed);
    else if (!NO_OPENER.includes(raw.intent)) opener = pick(OPENERS.fresh, seed);
    const followUps = (raw.followUps || []).filter((f) => !(context.askedQuestions || []).includes(normalize(f)));
    let text = opener ? opener + raw.text : raw.text;
    let links = raw.links || [];
    // After a few answers, invite the visitor to get in touch, once per conversation.
    let nudged = false;
    if (!context.nudged && (context.turn || 0) >= 2 && !NO_NUDGE.includes(raw.intent) && raw.intent !== 'fallback') {
      text += `\n\n—\n\n${pick(NUDGES, seed)}`;
      if (!links.some((l) => l.url.startsWith('mailto:'))) links = [...links, { label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }];
      nudged = true;
    }
    return { ...raw, text, links, followUps, nudged };
  };

  const ask = (message, context = {}) => {
    const norm = normalize(message);
    const toks = tokens(message).filter((t) => !STOP.has(t));
    if (!norm) return fallback({ message });
    const cacheable = !(FOLLOW_UP.test(norm) && toks.length <= 3);
    if (cacheable && memo.has(norm)) return decorate(memo.get(norm), message, context);
    const raw = resolve(norm, toks, message, context);
    if (cacheable && raw.intent !== 'fallback') memo.set(norm, raw);
    return decorate(raw, message, context);
  };

  const resolve = (norm, toks, message, context) => {

    // Short follow-up on the previous topic
    if (FOLLOW_UP.test(norm) && toks.length <= 3 && context.lastIntent && MORE[context.lastIntent]) {
      return run(byId[MORE[context.lastIntent]]);
    }

    // Specific project by name
    const project = findProject(norm, toks, projects);
    if (project) return projectAnswer(project);

    // Score intents
    const scored = compiled.scoreAll(norm, toks).filter((x) => x.s > 0).sort((a, b) => b.s - a.s);
    const top = scored[0];

    // Specific technology ("does he know docker") beats a weak generic match
    const tech = resolveTech(norm, toks, techVocab);
    if (tech && (!top || top.s < 4 || ['skills', 'frontend', 'backend', 'database', 'aiml', 'devops'].includes(top.i.id))) {
      return tech.kind === 'stack' ? techAnswer(tech.entry, tech.typed, tech.dist) : notInStackAnswer(tech.entry, tech.typed, tech.dist);
    }
    // "has he used <something not in his stack>" → say so, don't guess
    const techQ = norm.match(/\b(?:know|knows|use|used|uses|using|familiar with|experience with|experienced in|worked with|work with|comfortable with|proficient in|skilled in|good at|good with)\s+([a-z0-9+#.\- ]{2,30})\??$/);
    if (techQ && (!top || top.s < 4)) {
      const subject = techQ[1].replace(/\b(the|a|an|any|with|in)\b/g, ' ').trim();
      const knownTopic = tokens(subject).every((w) => STOP.has(w) || compiled.isKeyword(w));
      if (subject && !knownTopic && !tIndex.has(normalize(subject).replace(/\.js$/, '').replace(/js$/, ''))) {
        return {
          text: `I don't have **${subject}** in ${K.profile.firstName}'s stack, so I won't claim it. His core tools are ${K.skills.Frontend.slice(0, 2).join(', ')}, ${K.skills.Backend.slice(0, 2).join(', ')}, ${K.skills.Databases.slice(0, 2).join(', ')} and ${K.skills['GenAI & LLMs'].slice(0, 3).join(', ')}. If ${subject} matters for your role, ask him at **${K.contactInfo.email}**.`,
          links: [{ label: 'Email Rakshan', url: `mailto:${K.contactInfo.email}` }],
          followUps: ['What is his full tech stack?', 'Is he available for hire?'],
          intent: 'tech-unknown',
        };
      }
    }

    // Weak or no intent match → BM25 retrieval over the knowledge base
    if (!top || top.s < 2) {
      const found = retrieve(message);
      if (found) return found;
      if (!top || top.s < 1.5) return fallback({ message });
    }

    const primary = run(top.i, norm);
    // Two clearly separate asks joined by "and" → append the second answer briefly
    const second = /\band\b|&|,/.test(norm)
      ? scored.slice(1, 4).find((x) => x.s >= Math.max(2, top.s * 0.4) && !['greeting', 'thanks', 'bye', 'help', 'about'].includes(x.i.id))
      : null;
    if (second) {
      const extra = run(second.i);
      return {
        ...primary,
        text: `${primary.text}\n\n—\n\n${extra.text}`,
        links: [...(primary.links || []), ...(extra.links || [])].filter((l, idx, arr) => arr.findIndex((x) => x.url === l.url) === idx),
        followUps: primary.followUps || extra.followUps,
      };
    }
    return primary;
  };

  const explain = (message) => { const norm = normalize(message); const toks = tokens(message).filter((t) => !STOP.has(t)); return compiled.scoreAll(norm, toks).filter((x) => x.s > 0).sort((a, b) => b.s - a.s).map((x) => [x.i.id, +x.s.toFixed(2)]); };
  return { ask, explain, intents: INTENTS.map((i) => i.id), index: { passages: index.size, vocabulary: index.vocabulary } };
}

export const quickQuestions = [
  'What is Elmory?',
  'Why should we hire him?',
  'Tell me about Baton',
  "What's his tech stack?",
  'Tell me about his experience',
  'What projects has he built?',
  'Is he available for hire?',
  'What is his education?',
  'How can I contact him?',
  'Does he know Next.js?',
  'How does he use AI to ship faster?',
  'Is he a good fit for a backend role?',
];
